import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Eye, FileText, RefreshCw, ScanLine, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { getArquivos, deleteArquivo, formatDateTime, formatCNPJ } from "@/lib/ocorenApi";
import { UploadZone } from "@/components/UploadZone";
import { AvaliacaoModal } from "@/components/AvaliacaoModal";
import { FtpPanel } from "@/components/FtpPanel";

export default function Arquivos() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [avaliacaoId, setAvaliacaoId] = useState(null);
  const [avaliacaoOpen, setAvaliacaoOpen] = useState(false);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getArquivos();
      setData(res);
    } catch {
      toast.error("Falha ao carregar arquivos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onDelete = async (id, nome) => {
    if (!window.confirm(`Remover "${nome}" e suas movimentações?`)) return;
    try {
      await deleteArquivo(id);
      toast.success("Arquivo removido");
      refresh();
    } catch {
      toast.error("Falha ao remover");
    }
  };

  return (
    <div data-testid="page-arquivos" className="animate-fade-in-up">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#52525B] font-bold mb-2">
            Histórico
          </div>
          <h1 className="font-heading text-4xl font-bold text-[#09090B] tracking-tight">
            Arquivos Processados
          </h1>
          <p className="text-sm text-[#52525B] mt-2">
            {data.total} arquivo(s) processado(s) até o momento.
          </p>
        </div>
        <button
          data-testid="arquivos-refresh-btn"
          onClick={refresh}
          className="bg-white border border-[#E4E4E7] hover:border-[#09090B] text-[#09090B] px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2 transition-colors"
        >
          <RefreshCw size={14} />
          Atualizar
        </button>
      </div>

      <FtpPanel onSynced={refresh} />


      <div className="mb-8 bg-white border border-[#E4E4E7] rounded-md p-6">
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-bold">
            Importar
          </div>
          <h2 className="font-heading text-lg font-semibold text-[#09090B] mt-1">
            Adicionar novo arquivo
          </h2>
        </div>
        <UploadZone compact onUploaded={refresh} />
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
        <table className="data-table w-full text-sm" data-testid="arquivos-table">
          <thead>
            <tr>
              <th>Arquivo</th>
              <th>Transportadora</th>
              <th>CNPJ</th>
              <th>Formato</th>
              <th className="text-right">Movs.</th>
              <th className="text-right">Tamanho</th>
              <th>Processado em</th>
              <th>Avisos</th>
              <th className="w-40 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="text-center text-[#52525B] py-10">
                  Carregando…
                </td>
              </tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-[#52525B] py-10">
                  Nenhum arquivo processado ainda.
                </td>
              </tr>
            )}
            {!loading &&
              data.items.map((a, idx) => (
                <tr key={a.id} data-testid={`arquivo-row-${idx}`}>
                  <td>
                    <div className="flex items-center gap-2 text-[#09090B]">
                      <FileText size={14} className="text-[#52525B]" />
                      <span className="font-medium">{a.nome_arquivo}</span>
                    </div>
                  </td>
                  <td className="text-[#09090B]">{a.nome_transportadora || "—"}</td>
                  <td className="font-mono-data text-xs text-[#52525B]">
                    {formatCNPJ(a.cnpj_transportadora)}
                  </td>
                  <td>
                    <span className="inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-sm bg-[#FAFAFA] border border-[#E4E4E7] font-mono-data">
                      {a.formato || "—"}
                    </span>
                  </td>
                  <td className="text-right font-mono-data font-semibold text-[#09090B]">
                    {a.total_movimentacoes}
                  </td>
                  <td className="text-right font-mono-data text-xs text-[#52525B]">
                    {((a.tamanho_bytes || 0) / 1024).toFixed(1)} KB
                  </td>
                  <td className="text-[#52525B] text-xs">
                    {formatDateTime(a.data_processamento)}
                  </td>
                  <td>
                    {(a.warnings || []).length > 0 ? (
                      <button
                        onClick={() => {
                          setAvaliacaoId(a.id);
                          setAvaliacaoOpen(true);
                        }}
                        data-testid={`arquivo-warning-${idx}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-semibold hover:bg-amber-100 transition-colors"
                      >
                        <AlertTriangle size={11} />
                        {a.warnings.length} aviso(s)
                      </button>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-700">
                        OK
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid={`arquivo-avaliar-${idx}`}
                        onClick={() => {
                          setAvaliacaoId(a.id);
                          setAvaliacaoOpen(true);
                        }}
                        className="p-2 hover:bg-[#FAFAFA] rounded-md text-[#52525B] hover:text-[#09090B] transition-colors relative"
                        title="Avaliação estrutural"
                      >
                        <ScanLine size={14} />
                        {(a.warnings || []).length > 0 && (
                          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-500 rounded-full" />
                        )}
                      </button>
                      <button
                        data-testid={`arquivo-view-${idx}`}
                        onClick={() => navigate(`/ocorrencias?arquivo_id=${a.id}`)}
                        className="p-2 hover:bg-[#FAFAFA] rounded-md text-[#52525B] hover:text-[#09090B] transition-colors"
                        title="Ver movimentações"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        data-testid={`arquivo-delete-${idx}`}
                        onClick={() => onDelete(a.id, a.nome_arquivo)}
                        className="p-2 hover:bg-[#FAFAFA] rounded-md text-[#52525B] hover:text-[#F43F5E] transition-colors"
                        title="Remover"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <AvaliacaoModal
        arquivoId={avaliacaoId}
        open={avaliacaoOpen}
        onOpenChange={setAvaliacaoOpen}
      />
    </div>
  );
}
